import { useEffect, useState } from 'react';
import type { AuditOutcome } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { AlertTriangle, CheckCircle2, Flag, ListPlus, Loader2 } from 'lucide-react';
import { OUTCOME_OPTIONS, isoDateFromToday } from '../audits/auditStatus';

export interface CheckRef {
  questionId: number;
  text: string;
  section: string;
}

export interface FindingRef extends CheckRef {
  hasAction: boolean;
}

interface AuditSummaryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  missing: CheckRef[];
  findings: FindingRef[];
  actionsCount: number;
  /** Photos on checks, plus file_upload checks with their file. */
  evidenceCount: number;
  /** Template validity period; null = completed audits never expire. */
  validityDays: number | null;
  canComplete: boolean;
  onGoTo: (questionId: number) => void;
  onRaiseAction: (questionId: number) => void;
  /** Resolves null on success, or the reason it failed (shown in the dialog). */
  onComplete: (outcome: AuditOutcome, summary: string) => Promise<string | null>;
}

/**
 * Review & complete: what is still unanswered (Go to), the findings and
 * whether each has an action, the outcome and summary. Complete audit stays
 * disabled until every required check is answered and an outcome is chosen.
 */
export default function AuditSummaryDialog({
  open, onOpenChange, missing, findings, actionsCount, evidenceCount, validityDays, canComplete, onGoTo, onRaiseAction,
  onComplete,
}: AuditSummaryDialogProps) {
  const { formatDate } = useFormatters();
  const [outcome, setOutcome] = useState<AuditOutcome>('');
  const [summary, setSummary] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (open) { setSaving(false); setError(null); } }, [open]);

  const ready = missing.length === 0 && outcome !== '' && canComplete;

  const submit = async () => {
    if (!ready) return;
    setSaving(true);
    setError(null);
    const failure = await onComplete(outcome, summary.trim());
    setSaving(false);
    if (failure) setError(failure);
    else { setOutcome(''); setSummary(''); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review &amp; complete</DialogTitle>
          <DialogDescription>
            Completing the audit records it on the supplier&apos;s profile and starts its validity. The supplier is not told.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <section className="space-y-2" aria-label="Unanswered required checks">
            {missing.length === 0 ? (
              <p className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />All required checks are answered.
              </p>
            ) : (
              <>
                <p className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="h-4 w-4" />
                  {missing.length} required {missing.length === 1 ? 'check is' : 'checks are'} not answered
                </p>
                <ul className="divide-y rounded-md border">
                  {missing.map((m) => (
                    <li key={m.questionId} className="flex items-center justify-between gap-3 px-3 py-2 text-base">
                      <span className="min-w-0"><span className="text-muted-foreground">{m.section} · </span>{m.text}</span>
                      <Button variant="outline" onClick={() => onGoTo(m.questionId)}>Go to</Button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          <section className="space-y-2" aria-label="Findings">
            <p className="text-base font-medium">
              {findings.length} {findings.length === 1 ? 'finding' : 'findings'} · {actionsCount} {actionsCount === 1 ? 'action' : 'actions'}
              {' '}· {evidenceCount} {evidenceCount === 1 ? 'photo or file' : 'photos or files'}
            </p>
            {findings.length > 0 && (
              <ul className="divide-y rounded-md border">
                {findings.map((f) => (
                  <li key={f.questionId} className="flex items-center justify-between gap-3 px-3 py-2 text-base">
                    <span className="flex min-w-0 items-center gap-2">
                      <Flag className="h-3.5 w-3.5 shrink-0 text-red-600" />
                      <span className="truncate">{f.text}</span>
                    </span>
                    {f.hasAction ? (
                      <span className="whitespace-nowrap text-sm text-muted-foreground">Action raised</span>
                    ) : (
                      <Button variant="ghost" onClick={() => onRaiseAction(f.questionId)}>
                        <ListPlus className="h-4 w-4 mr-1.5" />Raise action
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <div className="space-y-2">
            <Label>Outcome *</Label>
            <Select value={outcome} onValueChange={(v) => setOutcome(v as AuditOutcome)}>
              <SelectTrigger className="h-12 text-base" aria-label="Outcome"><SelectValue placeholder="Choose the outcome" /></SelectTrigger>
              <SelectContent>
                {OUTCOME_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} className="py-3 text-base">{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="audit-summary">Summary</Label>
            <Textarea id="audit-summary" rows={3} className="text-base" value={summary} onChange={(e) => setSummary(e.target.value)}
              placeholder="Overall impression, key strengths and concerns" />
          </div>
          <p className="text-sm text-muted-foreground">
            {validityDays ? `Valid until ${formatDate(isoDateFromToday(validityDays))} once completed.` : 'This template has no validity period — the audit will not expire.'}
          </p>
          {error && (
            <p role="alert" className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />{error}
            </p>
          )}
          {!canComplete && (
            <p className="rounded-md bg-muted px-3 py-2 text-sm">
              Only users who can approve suppliers can complete an audit. Your answers are saved — ask an approver to complete it.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button size="lg" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Keep editing</Button>
          <Button size="lg" onClick={submit} disabled={!ready || saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Complete audit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
